/**
 * Client-side AES-GCM encryption for sensitive document photos (Aadhar, PAN, etc.).
 * Uses Web Crypto API. Set VITE_DOCUMENT_ENCRYPTION_KEY in .env (32-byte key as 64 hex chars or 44 base64 chars).
 */

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;
const TAG_LENGTH = 128;

let cachedKey = null;

function getKeyMaterial() {
  const raw = import.meta.env.VITE_DOCUMENT_ENCRYPTION_KEY;
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length < 32) return null;
  return trimmed;
}

/**
 * Get or create the CryptoKey for encryption/decryption. Returns null if key not configured.
 * @returns {Promise<CryptoKey|null>}
 */
export async function getEncryptionKey() {
  if (cachedKey) return cachedKey;
  const keyMaterial = getKeyMaterial();
  if (!keyMaterial) return null;

  try {
    let keyBytes;
    if (/^[0-9a-fA-F]{64}$/.test(keyMaterial)) {
      keyBytes = Uint8Array.from(
        keyMaterial.match(/.{2}/g).map((b) => parseInt(b, 16))
      );
    } else {
      keyBytes = Uint8Array.from(atob(keyMaterial), (c) => c.charCodeAt(0));
    }
    if (keyBytes.length !== 32) return null;

    cachedKey = await crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: ALGORITHM, length: KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
    return cachedKey;
  } catch {
    return null;
  }
}

/**
 * Whether document encryption is enabled (key is set).
 * @returns {Promise<boolean>}
 */
export async function isEncryptionEnabled() {
  const key = await getEncryptionKey();
  return key !== null;
}

/**
 * Encrypt a File or Blob. Returns a Blob: 12-byte IV + ciphertext (includes 16-byte auth tag).
 * @param {File|Blob} file
 * @returns {Promise<Blob|null>} Encrypted blob or null if encryption not available
 */
export async function encryptFile(file) {
  const key = await getEncryptionKey();
  if (!key) return null;

  const buffer = await file.arrayBuffer();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  const ciphertext = await crypto.subtle.encrypt(
    {
      name: ALGORITHM,
      iv,
      tagLength: TAG_LENGTH,
    },
    key,
    buffer
  );

  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);

  return new Blob([combined], { type: 'application/octet-stream' });
}

/**
 * Decrypt a Blob produced by encryptFile. Returns the original file content as Blob.
 * @param {Blob} encryptedBlob
 * @returns {Promise<Blob|null>} Decrypted blob or null on failure
 */
export async function decryptBlob(encryptedBlob) {
  const key = await getEncryptionKey();
  if (!key) return null;

  const arrayBuffer = await encryptedBlob.arrayBuffer();
  if (arrayBuffer.byteLength < IV_LENGTH + 16) return null;

  const iv = arrayBuffer.slice(0, IV_LENGTH);
  const ciphertext = arrayBuffer.slice(IV_LENGTH);

  try {
    const decrypted = await crypto.subtle.decrypt(
      {
        name: ALGORITHM,
        iv,
        tagLength: TAG_LENGTH,
      },
      key,
      ciphertext
    );
    return new Blob([decrypted]);
  } catch {
    return null;
  }
}

/**
 * Check if a storage URL refers to an encrypted file (by .enc extension).
 * @param {string} url
 * @returns {boolean}
 */
export function isEncryptedDocumentUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const pathname = new URL(url).pathname;
    return pathname.endsWith('.enc');
  } catch {
    return false;
  }
}
